import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { PreviewColumn } from '../../state/api/organisationEngineSlice';

/**
 * Layout primitives shared by the three row tables (error / update / failed):
 * a collapsible card frame, a height-capped scroll container, and the manifest
 * column header. Each table composes these the same way, differing only in
 * the `<tbody>` content and the cell-level styling.
 */

// `<details>` is the native, keyboard-accessible disclosure widget — no extra
// deps. We layer Tailwind on top: rotating chevron via `group-open:`, padded
// summary, and an `<h2>` inside the summary so the section keeps its
// semantic heading.
export const CollapsibleSection = ({
    testId,
    title,
    description,
    defaultOpen,
    children,
}: {
    testId: string;
    title: string;
    description?: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}) => (
    <details data-testid={testId} className='group rounded border border-grey-30 bg-white shadow-sm' open={defaultOpen}>
        <summary className='flex cursor-pointer list-none items-center gap-2 px-4 py-3 hover:bg-grey-10'>
            <ChevronRight className='h-4 w-4 shrink-0 transition-transform group-open:rotate-90' aria-hidden='true' />
            <div className='flex flex-col'>
                <h2 className='text-base font-semibold'>{title}</h2>
                {description && <span className='text-sm text-grey-70'>{description}</span>}
            </div>
        </summary>
        <div className='border-t border-grey-20 px-4 py-3'>{children}</div>
    </details>
);

// Caps the rendered table at ~60% of the viewport height before scrolling so
// a manifest with hundreds of error/update rows doesn't blow out the page.
export const ManifestRowsScroller = ({ children }: { children: React.ReactNode }) => (
    <div className='overflow-auto max-h-[60vh]'>{children}</div>
);

// Renders the header row from the engine-supplied column list — no hardcoded
// MANIFEST_COLUMNS on the client.
export const ManifestRowsTableHead = ({ columns }: { columns: PreviewColumn[] }) => (
    <thead className='sticky top-0 bg-white z-10'>
        <tr className='border-b border-grey-30 text-left text-xs uppercase tracking-wide text-grey-70'>
            <th className='p-2 w-12'>Row</th>
            {columns.map(col => (
                <th key={col.key} className='p-2'>
                    {col.label}
                </th>
            ))}
        </tr>
    </thead>
);
